import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useState } from "react";
import type { DocumentSubstitution } from "../api/generated";
import { hasPermission } from "../entities/access";
import { shellBootstrapQuery } from "./bootstrap";
import { formatDate, useSubmissionKey } from "./counterpartiesSupport";
import { Notice } from "./Notice";
import { Pagination } from "./Pagination";
import type { ShellServices } from "./services";
import { SubstitutionForm } from "./SubstitutionForm";
import {
  substitutionError,
  substitutionReasons,
  substitutionScopeOptions,
  substitutionStatuses,
} from "./substitutionsSupport";
import "./approvals.css";

export function SubstitutionSection({
  userId,
  scopes,
  services,
}: {
  userId: string;
  scopes: readonly unknown[];
  services: ShellServices;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  const rights = bootstrap?.permissions ?? [];
  const allowed = [
    "document-workflow.substitutions.manage",
    "document-workflow.substitutions.manage.any",
  ].some((permission) => hasPermission({ permissions: rights }, permission));
  const context = bootstrap?.allOrganizations
    ? "all"
    : bootstrap?.currentScope?.id;
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [confirmation, setConfirmation] = useState<DocumentSubstitution | null>(
    null,
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const sending = useRef(false);
  const key = useSubmissionKey();
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ["document-workflow", "substitutions", userId, context, page],
    queryFn: () =>
      services.substitutions!.list({ user_id: userId, page, per_page: 10 }),
    enabled: allowed && services.substitutions !== undefined,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const accessible = new Set(bootstrap?.scopes.map((scope) => scope.id) ?? []);
  const availableScopes = substitutionScopeOptions(scopes).filter((scope) =>
    accessible.has(scope.id),
  );
  async function cancel() {
    if (sending.current || !confirmation) return;
    sending.current = true;
    setPending(true);
    setError(null);
    try {
      await services.substitutions!.cancel(confirmation.id, key.current());
      setPending(false);
      key.reset();
      await Promise.all([
        client.invalidateQueries({ queryKey: ["document-workflow"] }),
        client.invalidateQueries({ queryKey: ["user", userId] }),
        client.invalidateQueries({ queryKey: ["users"] }),
      ]);
      setConfirmation(null);
    } catch (failure) {
      setPending(false);
      setError(failure);
      await list.refetch();
    } finally {
      sending.current = false;
    }
  }
  if (!allowed || !services.substitutions) return null;
  return (
    <section className="drawer-section approval-page" aria-label="Замещение">
      <h3>Замещение</h3>
      <p>
        Замещение действует на задания согласования и новые назначения. Статус
        отсутствия задаётся отдельно.
      </p>
      <button
        className="button button--secondary"
        type="button"
        onClick={() => setCreating(true)}
        disabled={creating || availableScopes.length === 0}
      >
        Назначить заместителя
      </button>
      {availableScopes.length === 0 ? (
        <p>Нет доступных КПО сотрудника для назначения замещения</p>
      ) : null}
      {list.isLoading ? <p aria-busy="true">Загружаем замещения…</p> : null}
      {list.isError ? (
        <Notice>
          Не удалось загрузить замещения.{" "}
          <button type="button" onClick={() => void list.refetch()}>
            Повторить
          </button>
        </Notice>
      ) : null}
      {list.isSuccess && list.data.data.length === 0 ? (
        <p>Замещений пока нет</p>
      ) : null}
      {list.data && list.data.data.length > 0 ? (
        <>
          <ul className="approval-round">
            {list.data.data.map((item) => (
              <li key={item.id} className="cp-card">
                <strong>
                  {item.substitute?.display_name ?? "Сотрудник недоступен"}
                </strong>
                <p>
                  {formatDate(item.starts_at)} — {formatDate(item.ends_at)}
                </p>
                <p>
                  {item.access_scope.name} · {substitutionReasons[item.reason]}{" "}
                  · {substitutionStatuses[item.status]}
                </p>
                {item.note ? (
                  <p className="approval-comment">{item.note}</p>
                ) : null}
                {item.status === "scheduled" || item.status === "active" ? (
                  <button
                    className="button button--text"
                    type="button"
                    onClick={() => {
                      key.reset();
                      setError(null);
                      setConfirmation(item);
                    }}
                  >
                    Отменить
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <Pagination
            page={list.data.meta.current_page}
            lastPage={list.data.meta.last_page}
            total={list.data.meta.total}
            perPage={10}
            label="Страницы замещений"
            busy={list.isFetching}
            onChange={setPage}
          />
        </>
      ) : null}
      {confirmation ? (
        <section
          className="cp-card"
          aria-label="Подтверждение отмены замещения"
        >
          <p>Задания, перенаправленные заместителю, вернутся сотруднику</p>
          {error ? <Notice>{substitutionError(error)}</Notice> : null}
          <div className="approval-actions">
            <button
              className="button button--danger"
              type="button"
              disabled={pending}
              onClick={() => void cancel()}
            >
              Подтвердить отмену
            </button>
            <button
              className="button button--secondary"
              type="button"
              disabled={pending}
              onClick={() => setConfirmation(null)}
            >
              Назад
            </button>
          </div>
        </section>
      ) : null}
      {creating ? (
        <SubstitutionForm
          userId={userId}
          scopes={availableScopes}
          services={services}
          onCancel={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      ) : null}
    </section>
  );
}

export function MySubstitutionPage({ services }: { services: ShellServices }) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  if (bootstrap.isLoading) return <p aria-busy="true">Загрузка…</p>;
  if (!bootstrap.data) return <Notice>Не удалось загрузить аккаунт</Notice>;
  return (
    <section className="workspace-page">
      <h1>Моё замещение</h1>
      <SubstitutionSection
        userId={bootstrap.data.user.id}
        scopes={bootstrap.data.user.scopes}
        services={services}
      />
    </section>
  );
}
